import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import Badge from '../../components/base/Badge';
import Button from '../../components/base/Button';
import { mockDashboardData, mockActivities } from '../../mocks/activities';

export default function Dashboard() {
  const navigate = useNavigate();
  const { totalActivities, activeActivities, totalBudget, totalSpent, totalRevenue, newMembers, avgConversionRate, riskAlerts } = mockDashboardData;

  const recentActivities = mockActivities.slice(0, 3);

  const formatCurrency = (amount: number) => {
    return `¥${(amount / 10000).toFixed(1)}万`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const handleCreateActivity = () => {
    // 导航到活动管理页面
    navigate('/activities');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">营销平台仪表盘</h1>
          <p className="text-gray-600">实时监控营销活动数据和运营指标</p>
        </div>

        {/* 核心指标卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-event-line text-blue-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{totalActivities}</h3>
            <p className="text-gray-600 text-sm">总活动数</p>
            <p className="text-green-600 text-xs mt-1">进行中 {activeActivities} 个</p>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalRevenue)}</h3>
            <p className="text-gray-600 text-sm">总营收</p>
            <p className="text-green-600 text-xs mt-1">预算使用率 {((totalSpent / totalBudget) * 100).toFixed(1)}%</p>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-add-line text-purple-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{formatNumber(newMembers)}</h3>
            <p className="text-gray-600 text-sm">新增会员</p>
            <p className="text-green-600 text-xs mt-1">转化率 {avgConversionRate}%</p>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-wallet-line text-orange-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalBudget)}</h3>
            <p className="text-gray-600 text-sm">总预算</p>
            <p className="text-blue-600 text-xs mt-1">已使用 {formatCurrency(totalSpent)}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* 最近活动 */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">最近活动</h2>
              <Button variant="ghost" size="sm">
                查看全部
                <i className="ri-arrow-right-line ml-1"></i>
              </Button>
            </div>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">{activity.name}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>{activity.startDate} - {activity.endDate}</span>
                      <Badge 
                        variant={activity.status === 'active' ? 'success' : activity.status === 'planning' ? 'warning' : 'gray'}
                      >
                        {activity.status === 'active' ? '进行中' : activity.status === 'planning' ? '筹备中' : '已完成'}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(activity.budget)}</p>
                    <p className="text-xs text-gray-600">进度 {activity.progress}%</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* 风险预警 */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">风险预警</h2>
              <Badge variant="error">{riskAlerts.length}</Badge>
            </div>
            <div className="space-y-4">
              {riskAlerts.map((alert) => (
                <div key={alert.id} className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${alert.severity === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 mb-1">{alert.message}</p>
                    <p className="text-xs text-gray-600">
                      <i className="ri-time-line mr-1"></i>
                      2小时前
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <i className="ri-more-2-line"></i>
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Button variant="outline" className="w-full">
                查看所有预警
              </Button>
            </div>
          </Card>
        </div>

        {/* 快速操作 */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-6">快速操作</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              className="p-6 h-auto flex-col space-y-2"
              onClick={handleCreateActivity}
            >
              <i className="ri-add-circle-line text-2xl"></i>
              <span>创建新活动</span>
            </Button>
            <Button variant="outline" className="p-6 h-auto flex-col space-y-2">
              <i className="ri-calendar-line text-2xl"></i>
              <span>查看营销日历</span>
            </Button>
            <Button variant="outline" className="p-6 h-auto flex-col space-y-2">
              <i className="ri-file-chart-line text-2xl"></i>
              <span>生成报告</span>
            </Button>
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
}
